'use client';

import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Home, Package, Globe, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import type { ProductData } from '@/lib/types';
import type { LandingPageEntry } from '@/components/glitch-ui/landing-page-combobox';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';

export const HOMEPAGE_AD_ID = '_homepage';
export const ONBOARDING_LINK_PREFIX = 'onboarding_';

const normalizeUrlForComparison = (url: string) =>
  url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

const getLabelFromUrl = (url: string): string => {
  try {
    const u = new URL(url);
    const segments = u.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return last
      ? last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : u.hostname;
  } catch {
    return url;
  }
};

interface LandingPageSelectProps {
  businessUrl: string;
  brandName: string;
  products: ProductData[];
  setStepValid: (valid: boolean) => void;
  selectedAdIds?: Set<string>;
  onSelectedAdIdsChange?: (ids: Set<string>) => void;
  onboardingLinks?: string[];
}

export const LandingPageSelect = ({
  businessUrl,
  brandName,
  products,
  setStepValid,
  selectedAdIds,
  onSelectedAdIdsChange,
  onboardingLinks,
}: LandingPageSelectProps) => {

  const normalizedBusinessUrl = businessUrl.replace(/\/$/, '');
  const [customLinks, setCustomLinks] = useState<string[]>([]);
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);
  const entryRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // All custom URLs: pre-existing from business + session-added, deduped, excluding homepage
  const allCustomUrls = [...(onboardingLinks || []), ...customLinks]
    .filter((url, idx, arr) => arr.indexOf(url) === idx)
    .filter((url) => normalizeUrlForComparison(url) !== normalizeUrlForComparison(businessUrl));

  const allEntries = [
    { id: HOMEPAGE_AD_ID, label: brandName, url: normalizedBusinessUrl, icon: Home, type: 'homepage' as const },
    ...(products || []).map((p) => ({
      id: p.id,
      label: p.name,
      url: (p.landing_page_url || normalizedBusinessUrl).replace(/\/$/, ''),
      icon: Package,
      type: 'product' as const,
    })),
    ...allCustomUrls.map((url) => ({
      id: `${ONBOARDING_LINK_PREFIX}${url}`,
      label: getLabelFromUrl(url),
      url,
      icon: Globe,
      type: 'custom' as const,
    })),
  ];

  // Only show entries that are in selectedAdIds (all are selected by default)
  const entries = selectedAdIds
    ? allEntries.filter((e) => selectedAdIds.has(e.id))
    : allEntries;

  const scrollToEntry = (entryId: string) => {
    const el = entryRefs.current.get(entryId);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedEntryId(entryId);
      setTimeout(() => setHighlightedEntryId(null), 1500);
    }, 50);
  };

  const handleComboboxSelect = (entry: LandingPageEntry | null) => {
    if (!entry) return;

    // lp_ (from assets) and custom_ (newly typed) both map to onboarding_ prefix
    const internalId =
      entry.id.startsWith('custom_') || entry.id.startsWith('lp_')
        ? `${ONBOARDING_LINK_PREFIX}${entry.url}`
        : entry.id; // '_homepage' or product id — kept as-is

    // Track URL in local state so it appears in the displayed list
    if (entry.id.startsWith('custom_') || entry.id.startsWith('lp_')) {
      setCustomLinks((prev) => (prev.includes(entry.url) ? prev : [...prev, entry.url]));
    }

    if (selectedAdIds && onSelectedAdIdsChange) {
      const next = new Set(selectedAdIds);
      next.add(internalId);
      onSelectedAdIdsChange(next);
    }

    setTimeout(() => scrollToEntry(internalId), 100);
  };

  const removeEntry = (id: string) => {
    if (!selectedAdIds || !onSelectedAdIdsChange) return;
    const next = new Set(selectedAdIds);
    next.delete(id);
    onSelectedAdIdsChange(next);
  };

  useEffect(() => {
    setStepValid(entries.length > 0);
  }, [entries.length, setStepValid]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      className="space-y-4"
    >
      <p className="text-gray-700 mb-2">
        Choose which website pages you wish to create ads for. Ad copy will be generated for each of your selected pages based on the copy of that page.
      </p>

      <BusinessLandingPageCombobox
        selectedUrl={null}
        onSelect={handleComboboxSelect}
        placeholder="Add a landing page..."
      />

      <div className="space-y-1 max-h-[40vh] overflow-y-auto">
        {entries.map((entry) => {
          const isHighlighted = highlightedEntryId === entry.id;
          const Icon = entry.icon;
          return (
            <div
              key={entry.id}
              ref={(el) => {
                if (el) entryRefs.current.set(entry.id, el);
                else entryRefs.current.delete(entry.id);
              }}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors border ${
                isHighlighted
                  ? 'bg-blue-50 border-[#290BD9] shadow-sm'
                  : 'bg-white shadow-sm border-gray-200'
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center">
                <Icon className="h-4 w-4 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-900 block">{entry.label}</span>
                <span className="text-xs text-gray-500 truncate block">{entry.url}</span>
              </div>
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-700 flex-shrink-0"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <button
                type="button"
                onClick={() => removeEntry(entry.id)}
                className="flex-shrink-0"
              >
                <Trash2 className="h-5 w-5 text-gray-400 hover:text-red-600 cursor-pointer" />
              </button>
            </div>
          );
        })}
      </div>

    </motion.div>
  );
};
